import React, { Component } from 'react';
import classNames from 'classnames';

import { injectIntl, intlShape } from '../../../util/reactIntl';
import { propTypes } from '../../../util/types';
import { formatMoney } from '../../../util/currency';
import { ensureListing } from '../../../util/data';
import { isPriceVariationsEnabled } from '../../../util/configHelpers';
import { types as sdkTypes } from '../../../util/sdkLoader';

import css from './SearchMapPriceLabel.module.css';

const { Money } = sdkTypes;

const FEE_PERCENT = parseFloat(process.env.REACT_APP_DEFAULT_CUSTOMER_FEE_PERCENT) || 8.5;
const MINIMUM_FEE = parseInt(process.env.REACT_APP_MINIMUM_CUSTOMER_FEE, 10) || 0;

/**
 * SearchMapPriceLabel component
 * TODO: change to functional component
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that extends the default class for the root element
 * @param {propTypes.listing} props.listing - The listing
 * @param {Function} props.onListingClicked - The function to handle the listing click
 * @param {Object} props.config - The configuration
 * @param {intlShape} props.intl - The intl object
 * @returns {JSX.Element}
 */
class SearchMapPriceLabel extends Component {
  shouldComponentUpdate(nextProps) {
    const currentListing = ensureListing(this.props.listing);
    const nextListing = ensureListing(nextProps.listing);
    const isSameListing = currentListing.id.uuid === nextListing.id.uuid;
    const hasSamePrice = currentListing.attributes.price === nextListing.attributes.price;
    const hasSameActiveStatus = this.props.isActive === nextProps.isActive;
    const hasSameRefreshToken =
      this.props.mapComponentRefreshToken === nextProps.mapComponentRefreshToken;

    return !(isSameListing && hasSamePrice && hasSameActiveStatus && hasSameRefreshToken);
  }

  render() {
    const {
      className,
      rootClassName,
      listing,
      onListingClicked,
      intl,
      isActive,
      config,
    } = this.props;
    const currentListing = ensureListing(listing);
    const { price, publicData, title } = currentListing.attributes;

    // Use best hourly rate from rental slots if available.
    const rentalSlots = publicData?.rental_slots;
    const hasPricingFromSlots = Array.isArray(rentalSlots) && rentalSlots.length > 0;
    const basePrice = (() => {
      if (hasPricingFromSlots && price) {
        const bestRateCents = Math.min(...rentalSlots.map(s => s.price / s.hours));
        return new Money(Math.ceil(bestRateCents / 100) * 100, price.currency);
      }
      return price;
    })();

    // Apply all-in price (base + customer fee, subject to minimum) before formatting.
    // Round up to the nearest whole dollar so the formatter never shows cents.
    const allInPrice = (() => {
      if (basePrice?.amount == null) return basePrice;
      const calculatedFee = Math.round(basePrice.amount * (FEE_PERCENT / 100));
      const actualFee = Math.max(calculatedFee, MINIMUM_FEE);
      const raw = basePrice.amount + actualFee;
      return new Money(Math.ceil(raw / 100) * 100, basePrice.currency);
    })();

    // Create formatted price if currency is known or alternatively show just the unknown currency.
    const formattedPrice =
      allInPrice && allInPrice.currency === config.currency
        ? formatMoney(intl, allInPrice)
        : price?.currency
        ? price.currency
        : null;

    // Strip trailing ".00" from whole-dollar amounts, then append "+" suffix when derived from rental slots.
    const cleanPrice = formattedPrice?.replace(/\.00$/, '') ?? formattedPrice;
    const displayPrice = hasPricingFromSlots && cleanPrice ? `${cleanPrice}+` : cleanPrice;

    const priceValue = displayPrice
      ? intl.formatMessage({ id: 'SearchMapPriceLabel.price' }, { priceValue: displayPrice })
      : null;

    const validListingTypes = config.listing.listingTypes;
    const foundListingTypeConfig = validListingTypes.find(
      conf => conf.listingType === publicData?.listingType
    );
    const isPriceVariationsInUse = isPriceVariationsEnabled(publicData, foundListingTypeConfig);
    const hasMultiplePriceVariants =
      isPriceVariationsInUse && publicData?.priceVariants?.length > 1;

    const priceMessage = hasMultiplePriceVariants
      ? intl.formatMessage({ id: 'SearchMapInfoCard.priceStartingFrom' }, { priceValue })
      : intl.formatMessage({ id: 'SearchMapInfoCard.price' }, { priceValue });

    const classes = classNames(rootClassName || css.root, className);
    const priceLabelClasses = classNames(css.priceLabel, {
      [css.mapLabelActive]: isActive,
      [css.noPriceSetLabel]: !formattedPrice,
    });
    const caretClasses = classNames(css.caret, { [css.caretActive]: isActive });

    const ariaLabel = priceValue
      ? priceMessage
      : intl.formatMessage({ id: 'SearchMapPriceLabel.screenreader.mapMarkerWithoutPrice' });

    return (
      <button
        className={classes}
        onClick={() => onListingClicked(currentListing)}
        aria-label={ariaLabel}
      >
        <div className={css.caretShadow} />
        <div className={priceLabelClasses}>{priceMessage}</div>
        <div className={caretClasses} />
      </button>
    );
  }
}

export default injectIntl(SearchMapPriceLabel);
